import React, { useState } from 'react';
import {
  X, Star, Globe, Zap,
  Database, Headphones, Shield, Smartphone, Check, AlertCircle
} from 'lucide-react';
import { auth, signInWithGoogle } from '../utils/firebase';
import UpiCheckout from './UpiCheckout';
import PayPalDirectCheckout from './PayPalDirectCheckout';
import '../App.css';

const PremiumModal = ({ onClose, onSuccess, onUpgrade, trigger = 'generic' }) => {
  const [isInternational, setIsInternational] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('annual');
  const [paypalError, setPaypalError] = useState('');

  const successHandler = onSuccess || onUpgrade;

  const planCatalog = isInternational
    ? {
        monthly: {
          currentPrice: '$0.99',
          oldPrice: '$1.99',
          suffix: '/mo',
          title: 'Monthly Pass',
          badge: 'Flexible',
          description: '30 days access',
          usdAmount: '0.99'
        },
        annual: {
          currentPrice: '$2.99',
          oldPrice: '$4.99',
          suffix: '/yr',
          title: 'Annual Pass',
          badge: 'BEST VALUE',
          description: 'Save ~50%',
          usdAmount: '2.99'
        },
        lifetime: {
          currentPrice: '$7.99',
          oldPrice: '$9.99',
          suffix: '/once',
          title: 'Lifetime',
          badge: 'Forever',
          description: 'One-time pay',
          usdAmount: '7.99'
        }
      }
    : {
        monthly: {
          currentPrice: '₹49',
          oldPrice: '₹99',
          suffix: '/mo',
          title: 'Monthly Pass',
          badge: 'Flexible',
          description: '30 days access'
        },
        annual: {
          currentPrice: '₹299',
          oldPrice: '₹499',
          suffix: '/yr',
          title: 'Annual Pass',
          badge: 'BEST VALUE',
          description: 'Save ~50%'
        },
        lifetime: {
          currentPrice: '₹799',
          oldPrice: '₹999',
          suffix: '/once',
          title: 'Lifetime',
          badge: 'Forever',
          description: 'One-time pay'
        }
      };

  const isLimitTrigger = trigger === 'limit';
  const isExpiredTrigger = trigger === 'expired';

  const heading = isLimitTrigger
    ? (
      <>
        Hit the <span className="text-sky-400">free limit.</span>
      </>
    )
    : isExpiredTrigger
      ? (
        <>
          Premium <span className="text-sky-400">expired.</span>
        </>
      )
      : (
        <>
          Go Premium,<br /><span className="text-sky-400">Go Limitless.</span>
        </>
      );

  const subText = isLimitTrigger
    ? "Free plan allows 1 saved playlist. Upgrade to unlock unlimited cloud playlists & uninterrupted playback."
    : isExpiredTrigger
      ? "Renew your pass now to restore uninterrupted streaming and boost presets."
      : "Unlock unlimited playlist saving, multi-view, and server failover with zero ads.";


  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-2.5 sm:p-4 md:p-6 overflow-y-auto animate-backdrop-fade"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl xl:max-w-6xl max-h-[92vh] bg-slate-900/95 border border-white/15 rounded-3xl shadow-2xl overflow-y-auto text-white animate-modal-pop my-auto"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3.5 right-3.5 z-50 p-2 rounded-full bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white border border-white/10 transition shadow-xl"
          title="Close Modal"
        >
          <X size={18} />
        </button>

        {/* Responsive Grid: Left Features Panel (Desktop) + Right Action Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[480px]">

          {/* ── LEFT PANEL: FEATURES & BENEFITS ── */}
          <div className="lg:col-span-5 p-5 sm:p-7 relative flex flex-col justify-between overflow-hidden bg-gradient-to-b from-blue-950/30 via-slate-900/60 to-slate-950/80">
            {/* Ambient Background Glow */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600/10 via-purple-600/5 to-transparent pointer-events-none -z-10" />

            <div className="space-y-4 relative z-10">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-2xl bg-blue-500/15 border border-blue-400/30 text-sky-400 shadow-md">
                  <Star size={24} fill="currentColor" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight leading-tight">{heading}</h2>
                  <p className="text-slate-400 text-xs hidden sm:block mt-0.5">{subText}</p>
                </div>
              </div>

              {/* Mobile Micro-Pills (Keeps Mobile compact without scrolling!) */}
              <div className="flex lg:hidden flex-wrap gap-1.5 pt-1 text-[10px] text-slate-300 font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1">
                  <Database size={11} className="text-emerald-400" /> Unlimited Playlists
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1">
                  <Zap size={11} className="text-amber-400" /> Zero Limits
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1">
                  <Headphones size={11} className="text-rose-400" /> 200% Audio Boost
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 flex items-center gap-1">
                  <Globe size={11} className="text-sky-400" /> 2x2 Multi-View
                </span>
              </div>

              {/* Full Feature Checklist (Shown on Desktop) */}
              <div className="hidden lg:grid grid-cols-1 gap-3 pt-3">
                <FeatureItem
                  icon={<Database className="text-emerald-400" />}
                  title={isLimitTrigger ? "Save Unlimited Playlists" : "Unlimited Playlist Library"}
                  desc={isLimitTrigger ? "Save and manage unlimited playlists." : "Stop deleting old lists to add new ones."}
                />
                <FeatureItem icon={<Zap className="text-amber-400" />} title="No Free Limit Interruptions" desc="Keep watching continuously with zero timeouts." />
                <FeatureItem icon={<Headphones className="text-rose-400" />} title="Web Audio Boost & EQ" desc="200% Super Boost & studio equalizer presets." />
                <FeatureItem icon={<Globe className="text-sky-400" />} title="2x2 Multi-View Grid" desc="Watch up to 4 live streams simultaneously." />
                <FeatureItem icon={<Smartphone className="text-purple-400" />} title="Multi-Device Cloud Resume" desc="Sync state across iPhone, Android, and Desktop." />
                <FeatureItem icon={<Shield className="text-cyan-400" />} title="Priority Proxy Failover" desc="Instant CORS failover routing for broken streams." />
              </div>
            </div>

            <div className="pt-3 hidden lg:block border-t border-white/5">
              <p className="text-[11px] text-slate-400">
                🔒 Safe &amp; verified checkout. Instant activation on confirmation.
              </p>
            </div>
          </div>

          {/* ── RIGHT PANEL: PLAN SELECTION & PAYMENT ── */}
          <div className="lg:col-span-7 p-4 sm:p-6 lg:p-7 bg-slate-950/85 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col justify-between space-y-3.5">
            
            {/* Header: Title + Region Toggle */}
            <div className="flex items-center justify-between gap-2 pt-1 sm:pt-0">
              <div>
                <h3 className="text-sm font-extrabold text-white">Choose Your Plan</h3>
                <p className="text-[10px] text-slate-400">Cancel or upgrade anytime</p>
              </div>

              {/* Region Selector Pills */}
              <div className="flex bg-slate-900 p-1 rounded-full border border-white/10 shrink-0">
                <button
                  onClick={() => setIsInternational(false)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${
                    !isInternational ? 'bg-sky-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <span>🇮🇳</span>
                  <span>India</span>
                </button>
                <button
                  onClick={() => setIsInternational(true)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${
                    isInternational ? 'bg-sky-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <span>🌍</span>
                  <span>Global</span>
                </button>
              </div>
            </div>

            {/* ── HORIZONTAL 3-COLUMN PLAN CARDS (Eliminates vertical stacking!) ── */}
            <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
              
              {/* Monthly Plan */}
              <div
                onClick={() => setSelectedPlanId('monthly')}
                className={`relative p-2.5 sm:p-3 rounded-2xl cursor-pointer transition-all border flex flex-col justify-between ${
                  selectedPlanId === 'monthly'
                    ? 'bg-blue-600/15 border-blue-500 shadow-lg shadow-blue-500/20'
                    : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                }`}
              >
                <div>
                  <span className="block text-[11px] font-bold text-slate-300 truncate">{planCatalog.monthly.title}</span>
                  <div className="text-base sm:text-lg font-black text-white mt-0.5">
                    {planCatalog.monthly.currentPrice}
                    <span className="text-[10px] text-slate-400 font-normal">{planCatalog.monthly.suffix}</span>
                  </div>
                </div>
                <span className="text-[9px] text-slate-400 mt-1 truncate">{planCatalog.monthly.description}</span>
              </div>

              {/* Annual Plan (Featured) */}
              <div
                onClick={() => setSelectedPlanId('annual')}
                className={`relative p-2.5 sm:p-3 rounded-2xl cursor-pointer transition-all border flex flex-col justify-between ${
                  selectedPlanId === 'annual'
                    ? 'bg-blue-600/20 border-blue-400 shadow-xl shadow-blue-500/25 ring-1 ring-blue-400/50'
                    : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.2 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 text-[8px] font-black uppercase tracking-wider text-white shadow-md whitespace-nowrap">
                  MOST POPULAR
                </div>
                <div>
                  <span className="block text-[11px] font-bold text-white truncate">{planCatalog.annual.title}</span>
                  <div className="text-base sm:text-lg font-black text-white mt-0.5">
                    {planCatalog.annual.currentPrice}
                    <span className="text-[10px] text-sky-300 font-normal">{planCatalog.annual.suffix}</span>
                  </div>
                </div>
                <span className="text-[9px] text-amber-300 font-semibold mt-1 truncate">⭐ Save ~50%</span>
              </div>

              {/* Lifetime Plan */}
              <div
                onClick={() => setSelectedPlanId('lifetime')}
                className={`relative p-2.5 sm:p-3 rounded-2xl cursor-pointer transition-all border flex flex-col justify-between ${
                  selectedPlanId === 'lifetime'
                    ? 'bg-blue-600/15 border-blue-500 shadow-lg shadow-blue-500/20'
                    : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                }`}
              >
                <div>
                  <span className="block text-[11px] font-bold text-slate-300 truncate">{planCatalog.lifetime.title}</span>
                  <div className="text-base sm:text-lg font-black text-white mt-0.5">
                    {planCatalog.lifetime.currentPrice}
                    <span className="text-[10px] text-slate-400 font-normal">{planCatalog.lifetime.suffix}</span>
                  </div>
                </div>
                <span className="text-[9px] text-slate-400 mt-1 truncate">{planCatalog.lifetime.description}</span>
              </div>

            </div>

            {/* ── CHECKOUT CONTAINER ── */}
            <div>
              {!isInternational ? (
                <UpiCheckout
                  key={`upi_${selectedPlanId}`}
                  planId={selectedPlanId}
                  onVerified={() => {
                    if (successHandler) successHandler(selectedPlanId);
                    else {
                      onClose();
                      window.location.reload();
                    }
                  }}
                />
              ) : !auth.currentUser ? (
                <div className="p-5 rounded-2xl bg-slate-900/80 border border-sky-500/20 text-center space-y-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-400/30 text-sky-400 flex items-center justify-center mx-auto">
                    <Globe size={20} />
                  </div>
                  <div>
                    <h4 className="text-sm font-extrabold text-white">Sign In Required for PayPal</h4>
                    <p className="text-xs text-slate-400">Please sign in with your Google account first so your pass is instantly credited to your profile upon verification.</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      setPaypalError('');
                      try {
                        await signInWithGoogle();
                      } catch (e) {
                        setPaypalError("Sign in failed. Please try again.");
                      }
                    }}
                    className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-extrabold text-xs shadow-md transition"
                  >
                    Sign In with Google
                  </button>
                </div>
              ) : (
                <PayPalDirectCheckout
                  key={`paypal_${selectedPlanId}`}
                  planId={selectedPlanId}
                  onVerified={() => {
                    if (successHandler) successHandler(selectedPlanId);
                    else {
                      onClose();
                      window.location.reload();
                    }
                  }}
                />
              )}
            </div>

            {/* Guarantee Footer */}
            <div className="text-center pt-2 border-t border-white/10">
              <p className="text-[10px] text-slate-400">
                7-Day Money-Back Guarantee. <a href="/refund" className="text-sky-300 underline">View policy</a>
              </p>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

const FeatureItem = ({ icon, title, desc }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-7 h-7 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      {React.cloneElement(icon, { size: 15 })}
    </div>
    <div>
      <h4 className="text-xs font-bold text-white">{title}</h4>
      <p className="text-[10px] text-slate-400 leading-tight">{desc}</p>
    </div>
  </div>
);

export default PremiumModal;
